import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Save, RotateCcw, Eye, Palette, Building2, ShieldCheck, User } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { api, formatApiError } from "../lib/api";
import { useBranding, applyTheme } from "../lib/branding";

const PRESETS = [
    { name: "Navy (default)", color: "#0B192C" },
    { name: "Royal Blue", color: "#1E3A8A" },
    { name: "Forest Green", color: "#14532D" },
    { name: "Wine", color: "#7F1D1D" },
    { name: "Charcoal", color: "#1F2937" },
    { name: "Plum", color: "#581C87" },
];

export default function AdminBranding() {
    const navigate = useNavigate();
    const { branding, refresh, applyPreview } = useBranding();
    const [form, setForm] = useState(branding);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => { setForm(branding); }, [branding]);

    const set = (k, v) => {
        const next = { ...form, [k]: v };
        setForm(next);
        applyPreview({ [k]: v });
    };

    const onLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            set("logo_url", fullUrl);
            toast.success("Logo uploaded");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    const onOwnerPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            set("owner_photo_url", fullUrl);
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/admin/branding", form);
            toast.success("Branding saved");
            await refresh();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const resetPreview = () => {
        setForm({ ...branding });
        applyTheme(branding.primary_color);
        document.title = branding.organization_name || "Your Annuity Review";
    };

    return (
        <div className="min-h-screen bg-slate-50" data-testid="admin-branding-page">
            <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="outline" onClick={() => navigate("/admin")} data-testid="back-to-leads" className="rounded-md">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Leads
                        </Button>
                        <div>
                            <div className="font-serif text-xl text-navy-900 flex items-center gap-2"><Palette className="w-5 h-5" /> White-label Branding</div>
                            <div className="text-sm text-slate-700">Customize the platform for your FMO or each prospect demo</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={resetPreview} data-testid="reset-preview" className="rounded-md">
                            <RotateCcw className="w-4 h-4 mr-2" /> Discard changes
                        </Button>
                        <Button onClick={save} disabled={saving} className="bg-navy-900 hover:bg-navy-800 text-white rounded-md" data-testid="save-branding">
                            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save"}
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-12 gap-10">
                {/* Form */}
                <div className="lg:col-span-7 space-y-8">
                    <Section title="Identity" icon={Building2}>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Organization Name">
                                <Input value={form.organization_name || ""} onChange={(e) => set("organization_name", e.target.value)} data-testid="brand-org-name" placeholder="e.g. Acme FMO Group" />
                            </Field>
                            <Field label="Tagline / Domain">
                                <Input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="brand-tagline" placeholder="acmefmo.com" />
                            </Field>
                        </div>
                        <Field label="Logo">
                            <div className="flex items-center gap-4">
                                {form.logo_url ? (
                                    <img src={form.logo_url} alt="" className="w-16 h-16 rounded-md object-cover border border-slate-200 bg-white" />
                                ) : (
                                    <div className="w-16 h-16 rounded-md bg-slate-100 flex items-center justify-center text-slate-600">
                                        <Upload className="w-5 h-5" />
                                    </div>
                                )}
                                <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-slate-200 rounded-md text-sm font-medium hover:bg-slate-50">
                                    <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading..." : (form.logo_url ? "Replace" : "Upload Logo")}
                                    <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="brand-logo-upload" />
                                </label>
                                {form.logo_url && (
                                    <Button variant="ghost" size="sm" onClick={() => set("logo_url", "")} data-testid="brand-logo-clear">Clear</Button>
                                )}
                            </div>
                            <div className="mt-2 text-xs text-slate-700">PNG, JPG or SVG. Max 5MB. Square format works best.</div>
                        </Field>
                    </Section>

                    <Section title="Color" icon={Palette}>
                        <Field label="Primary Color">
                            <div className="flex items-center gap-3">
                                <input
                                    type="color"
                                    value={form.primary_color || "#0B192C"}
                                    onChange={(e) => set("primary_color", e.target.value)}
                                    className="w-14 h-12 border border-slate-200 rounded-md cursor-pointer"
                                    data-testid="brand-color-picker"
                                />
                                <Input value={form.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} placeholder="#0B192C" className="flex-1 font-mono" data-testid="brand-color-hex" />
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {PRESETS.map((p) => (
                                    <button
                                        key={p.color}
                                        type="button"
                                        onClick={() => set("primary_color", p.color)}
                                        className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-md text-sm hover:bg-slate-50"
                                        data-testid={`brand-preset-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                                    >
                                        <span className="w-4 h-4 rounded" style={{ backgroundColor: p.color }} />
                                        {p.name}
                                    </button>
                                ))}
                            </div>
                            <div className="mt-2 text-xs text-slate-700">Applies to header CTA, hero background, footer background, primary buttons.</div>
                        </Field>
                    </Section>

                    <Section title="Hero Copy" icon={Eye}>
                        <Field label="Headline">
                            <Input value={form.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} data-testid="brand-hero-headline" />
                        </Field>
                        <Field label="Subheadline">
                            <Textarea rows={2} value={form.hero_subheadline || ""} onChange={(e) => set("hero_subheadline", e.target.value)} data-testid="brand-hero-sub" />
                        </Field>
                    </Section>

                    <Section title="Featured Advisor (Your Profile)" icon={User}>
                        <div className="text-sm text-slate-600 -mt-2 mb-2">Appears prominently on the homepage right under the hero. Leave name blank to hide this section.</div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Your Name">
                                <Input value={form.owner_name || ""} onChange={(e) => set("owner_name", e.target.value)} data-testid="brand-owner-name" placeholder="e.g. Mark Stevens" />
                            </Field>
                            <Field label="Title / Credentials">
                                <Input value={form.owner_title || ""} onChange={(e) => set("owner_title", e.target.value)} data-testid="brand-owner-title" placeholder="Licensed Insurance Professional" />
                            </Field>
                            <Field label="Phone">
                                <Input value={form.owner_phone || ""} onChange={(e) => set("owner_phone", e.target.value)} data-testid="brand-owner-phone" placeholder="(405) 555-0101" />
                            </Field>
                            <Field label="Email">
                                <Input value={form.owner_email || ""} onChange={(e) => set("owner_email", e.target.value)} data-testid="brand-owner-email" placeholder="mark@yourannuityreview.com" />
                            </Field>
                            <Field label="Calendly Link">
                                <Input value={form.owner_calendly_link || ""} onChange={(e) => set("owner_calendly_link", e.target.value)} data-testid="brand-owner-calendly" placeholder="https://calendly.com/..." />
                            </Field>
                            <Field label="States Licensed In">
                                <Input value={form.owner_states || ""} onChange={(e) => set("owner_states", e.target.value)} data-testid="brand-owner-states" placeholder="OK, KS, TX" />
                            </Field>
                        </div>
                        <Field label="Short Bio">
                            <Textarea rows={3} value={form.owner_bio || ""} onChange={(e) => set("owner_bio", e.target.value)} data-testid="brand-owner-bio" placeholder="One or two sentences about your background and approach." />
                        </Field>
                        <Field label="Profile Photo">
                            <div className="flex items-center gap-4">
                                {form.owner_photo_url ? (
                                    <img src={form.owner_photo_url} alt="" className="w-20 h-20 rounded-full object-cover object-top border border-slate-200" />
                                ) : (
                                    <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                                        <Upload className="w-5 h-5" />
                                    </div>
                                )}
                                <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-slate-200 rounded-md text-sm font-medium hover:bg-slate-50">
                                    <Upload className="w-4 h-4 mr-2" /> {form.owner_photo_url ? "Replace" : "Upload Photo"}
                                    <input type="file" accept="image/*" className="hidden" onChange={onOwnerPhoto} data-testid="brand-owner-photo-upload" />
                                </label>
                                {form.owner_photo_url && (
                                    <Button variant="ghost" size="sm" onClick={() => set("owner_photo_url", "")} data-testid="brand-owner-photo-clear">Clear</Button>
                                )}
                            </div>
                            <div className="mt-2 text-xs text-slate-700">JPG/PNG, max 5MB. Square works best.</div>
                        </Field>
                    </Section>

                    <Section title="Contact & Legal" icon={ShieldCheck}>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Support Email">
                                <Input value={form.support_email || ""} onChange={(e) => set("support_email", e.target.value)} data-testid="brand-support-email" placeholder="reviews@acmefmo.com" />
                            </Field>
                            <Field label="Support Phone">
                                <Input value={form.support_phone || ""} onChange={(e) => set("support_phone", e.target.value)} data-testid="brand-support-phone" placeholder="(555) 123-4567" />
                            </Field>
                        </div>
                        <Field label="Footer Disclaimer">
                            <Textarea rows={3} value={form.footer_disclaimer || ""} onChange={(e) => set("footer_disclaimer", e.target.value)} data-testid="brand-disclaimer" />
                        </Field>
                    </Section>
                </div>

                {/* Live Preview */}
                <div className="lg:col-span-5">
                    <div className="lg:sticky lg:top-24 space-y-4">
                        <div className="uppercase tracking-[0.25em] text-xs font-semibold text-slate-700">Live Preview</div>

                        {/* Mini header */}
                        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="preview-header">
                            <div className="flex items-center justify-between p-4 border-b border-slate-200">
                                <div className="flex items-center gap-3">
                                    {form.logo_url ? (
                                        <img src={form.logo_url} alt="" className="w-9 h-9 rounded-md object-cover border border-slate-200" />
                                    ) : (
                                        <div className="w-9 h-9 rounded-md bg-navy-900 flex items-center justify-center text-white"><ShieldCheck className="w-4 h-4" /></div>
                                    )}
                                    <div>
                                        <div className="font-serif text-base text-navy-900 font-semibold leading-tight">{form.organization_name || "Organization"}</div>
                                        <div className="uppercase tracking-[0.2em] text-[9px] text-slate-700">{form.tagline}</div>
                                    </div>
                                </div>
                                <button className="bg-navy-900 hover:bg-navy-800 text-white text-xs font-medium rounded-md px-4 py-2">Get My Free Review</button>
                            </div>

                            {/* Mini hero */}
                            <div className="bg-navy-900 text-white p-8 relative overflow-hidden">
                                <div className="absolute inset-0 grain opacity-20" />
                                <div className="relative">
                                    <div className="uppercase tracking-[0.2em] text-[10px] font-semibold text-blue-200/80 mb-3">Free Annuity Review</div>
                                    <div className="font-serif text-2xl leading-tight">{form.hero_headline || "Stop Guessing About Your Annuity."}</div>
                                    <div className="mt-3 text-sm text-blue-100/85 leading-relaxed">{form.hero_subheadline}</div>
                                    <div className="mt-5 flex gap-2">
                                        <button className="bg-white text-navy-900 text-xs font-semibold rounded-md px-4 py-2">Get My Free Review →</button>
                                        <button className="border border-white/40 text-white text-xs font-medium rounded-md px-4 py-2">How It Works</button>
                                    </div>
                                </div>
                            </div>

                            {/* Mini footer */}
                            <div className="bg-navy-900 text-white p-5 border-t border-white/10">
                                <div className="flex items-center justify-between">
                                    <div className="text-xs">© {new Date().getFullYear()} {form.organization_name}</div>
                                    <div className="text-[10px] text-blue-100/60">{form.support_email}</div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
                            <strong className="text-slate-900">Tip:</strong> Edit fields on the left and watch the preview update in real time. Click <em>Save</em> to persist for everyone, or <em>Discard changes</em> to revert to what's currently saved.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 text-navy-900 font-serif text-lg mb-5"><Icon className="w-5 h-5" /> {title}</div>
            <div className="space-y-5">{children}</div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-sm text-slate-700">{label}</Label>
            <div className="mt-1.5">{children}</div>
        </div>
    );
}
